/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getEligibleCustomersTotalAsync } from './ContactActions/DataActionExtension.g';
import { IGetEligibleCustomersTotalResponse } from './ContactActions/DataServiceEntities.g';

/**
 * Input class for get customer data action.
 */
export class CustomersTotalInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('CustomersTotal', this.apiSettings);

    public getCacheObjectType = () => 'CustomersTotal';

    public dataCacheType = (): CacheType => 'none';
}

export const createGetCustomersTotalInput = (inputData: ICreateActionContext): IActionInput => {
    const { requestContext } = inputData;

    // Check if the user is authenticated.
    if (!requestContext.user.isAuthenticated) {
        throw new Error('User is not logged in.');
    }

    return new CustomersTotalInput(requestContext.apiSettings);
};

export async function getCustomersTotal(input: CustomersTotalInput, ctx: IActionContext): Promise<IGetEligibleCustomersTotalResponse> {
    const cartId = ctx.requestContext.cookies.getCartCookie();
    const checkoutCartId = ctx.requestContext.cookies.getCheckoutCartCookie();
    return getEligibleCustomersTotalAsync({ callerContext: ctx }, cartId?.split(':')[1] || '', checkoutCartId?.split(':')[1] || '')
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[get-customers-total] Unable to load customers total');
            throw new Error('[get-customers-total] Unable to load customers total');
        });
}

export const getCustomersTotalDataAction = createObservableDataAction({
    id: 'get-customer-total',
    action: <IAction<IGetEligibleCustomersTotalResponse>>(<unknown>getCustomersTotal),
    input: createGetCustomersTotalInput
});

export default getCustomersTotalDataAction;
